import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { SidebarStorage } from "@/components/StoragePill";
import { SidebarResume } from "@/components/ContinueListening";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { BookOpen, ChevronDown, Clock, Disc, Users, Music, Heart, ListMusic, Sparkles, Upload, AudioLines, Bookmark, Radio, CloudDownload, Copy, Clapperboard, Pin, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlaylists } from "@/context/PlaylistsContext";
import { useAutoPins } from "@/lib/autoPins";
import { isCollapsed, pinItem, pinsOf, toggleGroup, unpinItem, useSidebarPrefs } from "@/lib/sidebarPrefs";

const RECENT = { to: "/albums?sort=recent", label: "Recently Added", icon: Clock, id: "recently-added", match: (p, s) => p === "/albums" && (!s || s.includes("sort=recent")) };
const FAVORITES = { to: "/favorites", label: "Favorites", icon: Heart, id: "favorites" };
const LIBRARY = [
  { to: "/albums?sort=name", label: "Albums", icon: Disc, id: "albums", match: (p, s) => p.startsWith("/albums") && !!s && !s.includes("sort=recent") },
  { to: "/artists", label: "Artists", icon: Users, id: "artists" },
  { to: "/songs", label: "Songs", icon: Music, id: "songs" },
  { to: "/playlists", label: "Playlists", icon: ListMusic, id: "playlists" },
  { to: "/radio", label: "Radio", icon: Radio, id: "radio" },
  { to: "/audiobooks", label: "Audiobooks", icon: BookOpen, id: "audiobooks" },
  { to: "/videos", label: "Videos", icon: Clapperboard, id: "videos" },
];
const TOOLS = [
  { to: "/builder", label: "Playlist Builder", icon: Sparkles, id: "playlist-builder", badge: "AI" },
  { to: "/wishlist", label: "Wishlist", icon: Bookmark, id: "wishlist" },
  { to: "/duplicates", label: "Duplicates", icon: Copy, id: "duplicates" },
  { to: "/offline", label: "Offline music", icon: CloudDownload, id: "offline" },
  { to: "/upload", label: "Upload music", icon: Upload, id: "upload" },
];

const toolsFor = () => TOOLS;

const DRAG = "text/rh-sidebar-item";

const Item = ({ item, badge, prefix = "nav-item", onDragState, pinned = false }) => {
  const { pathname, search } = useLocation();
  const Icon = item.icon;
  const shown = badge ?? item.badge;
  const active = item.match ? item.match(pathname, search) : pathname.startsWith(item.to);
  return (
    <NavLink
      to={item.to}
      data-testid={item.testid || `${prefix}-${item.id}`}
      data-active={active ? "1" : undefined}
      data-pinned={pinned ? "1" : undefined}
      draggable={!!onDragState}
      onDragStart={onDragState ? (e) => { e.dataTransfer.setData(DRAG, item.id); e.dataTransfer.effectAllowed = "move"; onDragState(item.id); } : undefined}
      onDragEnd={onDragState ? () => onDragState(null) : undefined}
      title={onDragState && !pinned ? `${item.label} — drag to the top to pin` : undefined}
      className={`group relative flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors duration-150 ${
        active ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
      } ${onDragState ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <Icon size={16} strokeWidth={1.75} className="shrink-0" />
      <span className="flex-1 truncate">{item.label}</span>
      {shown && (
        <span data-testid={`nav-badge-${item.id}`} title={badge ? "A download failed — open Offline music" : undefined} className={`rounded px-1.5 py-0.5 font-mono text-[10px] ${badge ? "bg-red-600 text-white" : active ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-700"}`}>
          {shown}
        </span>
      )}
      {pinned && (
        <button type="button" aria-label={`Unpin ${item.label}`} data-testid={`unpin-${item.id}`} title="Unpin"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); unpinItem(item.id); }}
          className={`rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 ${active ? "hover:bg-white/20" : "hover:bg-zinc-200"}`}>
          <X size={12} />
        </button>
      )}
    </NavLink>
  );
};

/* Drop target above the groups. Items dropped here are pinned (and leave their group); dropping onto a pinned item inserts before it. */
const PinStrip = ({ pins, dragging, byId, onDragState }) => {
  const [over, setOver] = useState(null); // index being hovered, or "end"
  const items = pins.map((id) => byId[id]).filter(Boolean);
  const draggingNew = dragging && !pins.includes(dragging);
  if (items.length === 0 && !dragging) return null;
  const drop = (e, index) => {
    e.preventDefault(); e.stopPropagation();
    const id = e.dataTransfer.getData(DRAG);
    setOver(null);
    if (id) pinItem(id, index);
  };
  const allow = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== idx) setOver(idx); };
  return (
    <div data-testid="sidebar-pinned" data-dragging={dragging ? "1" : undefined} onDragOver={(e) => allow(e, "end")} onDragLeave={() => setOver(null)} onDrop={(e) => drop(e, null)}
      className={`mb-2 rounded-lg transition-colors ${dragging ? "bg-amber-50/70 ring-1 ring-dashed ring-amber-300" : ""} ${items.length === 0 ? "p-1" : ""}`}>
      <p className="sidebar-label flex items-center gap-1.5 px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400"><Pin size={10} />Pinned</p>
      <div className="space-y-0.5">
        {items.map((item, i) => (
          <div key={item.id} onDragOver={(e) => allow(e, i)} onDrop={(e) => drop(e, i)} className={`rounded-md transition-shadow ${over === i && dragging && dragging !== item.id ? "shadow-[0_-2px_0_0_#f59e0b]" : ""}`}>
            <Item item={item} pinned onDragState={onDragState} />
          </div>
        ))}
      </div>
      {draggingNew && <p className="px-3 py-2 text-center text-[11px] text-amber-700" data-testid="sidebar-pin-hint">Drop here to pin</p>}
    </div>
  );
};

const Label = ({ id, children, collapsed, onToggle, count }) => (
  <button type="button" onClick={onToggle} aria-expanded={!collapsed} data-testid={`sidebar-group-${id}`} data-collapsed={collapsed ? "1" : "0"}
    className="sidebar-label sticky top-0 z-10 -mx-1 mb-1.5 mt-5 flex w-[calc(100%+0.5rem)] items-center gap-1.5 bg-[#F6F5F0]/95 px-4 py-1.5 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400 backdrop-blur-sm transition-colors first:mt-0 hover:text-zinc-700">
    <span className="flex-1">{children}</span>
    {collapsed && count > 0 && <span className="font-mono tracking-normal text-zinc-400" data-testid={`sidebar-group-count-${id}`}>{count}</span>}
    <ChevronDown size={12} strokeWidth={2} className={`transition-transform duration-200 ${collapsed ? "-rotate-90" : ""}`} />
  </button>
);

const Group = ({ id, label, children, items, popup }) => {
  const prefs = useSidebarPrefs();
  const collapsed = isCollapsed(prefs, id);
  const [open, setOpen] = useState(false);
  const body = (
    <div data-testid={`sidebar-group-wrap-${id}`} data-collapsed={collapsed ? "1" : "0"}>
      <Label id={id} collapsed={collapsed} count={items} onToggle={() => { setOpen(false); toggleGroup(id); }}>{label}</Label>
      <div data-testid={`sidebar-group-body-${id}`} data-collapsed={collapsed ? "1" : "0"} className="space-y-0.5">{children(collapsed)}</div>
    </div>
  );
  if (!collapsed || !popup) return body;
  // Folded: hovering the header or the highlighted item pops the full list out to the right — no need to expand.
  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={200} closeDelay={150}>
      <HoverCardTrigger asChild>{body}</HoverCardTrigger>
      <HoverCardPrimitive.Portal>
        <HoverCardContent side="right" align="start" sideOffset={14} className="w-56 border-zinc-200 bg-[#F6F5F0]/95 p-1.5 backdrop-blur-md" data-testid={`sidebar-popup-${id}`}>
          <p className="px-3 pb-1.5 pt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
          <div className="space-y-0.5" onClick={() => setOpen(false)}>{popup()}</div>
        </HoverCardContent>
      </HoverCardPrimitive.Portal>
    </HoverCard>
  );
};

const useSidebarScroll = (deps) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      el.dataset.canUp = el.scrollTop > 4 ? "1" : "0";
      el.dataset.canDown = el.scrollTop + el.clientHeight < el.scrollHeight - 4 ? "1" : "0";
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
  }, []);
  useEffect(() => {
    const el = ref.current;
    const active = el?.querySelector('[data-active="1"], a[aria-current="page"]');
    if (!el || !active) return;
    const top = active.offsetTop;
    const pad = 40;
    if (top < el.scrollTop + pad || top + active.offsetHeight > el.scrollTop + el.clientHeight - pad) {
      el.scrollTo({ top: Math.max(0, top - el.clientHeight / 2 + active.offsetHeight / 2), behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
};

export const Sidebar = () => {
  const { user } = useAuth();
  const { playlists } = usePlaylists();
  const { status } = useAutoPins();
  const { pathname, search } = useLocation();
  const prefs = useSidebarPrefs();
  const [dragging, setDragging] = useState(null);
  const isActive = (i) => (i.match ? i.match(pathname, search) : pathname.startsWith(i.to));
  const scrollRef = useSidebarScroll([pathname, search, playlists.length]);
  const plItems = playlists.slice(0, 12).map((p) => ({ to: `/playlists/${p.id}`, label: p.name, icon: ListMusic, id: `playlist:${p.id}`, testid: `sidebar-playlist-${p.id}` }));
  const byId = Object.fromEntries([RECENT, FAVORITES, ...LIBRARY, ...toolsFor(user), ...plItems].map((i) => [i.id, i]));
  const pins = pinsOf(prefs).filter((id) => byId[id]);
  const unpinned = (list) => list.filter((i) => !pins.includes(i.id));
  const lib = unpinned(LIBRARY), tools = unpinned(toolsFor(user)), pls = unpinned(plItems);
  const dragProps = { onDragState: setDragging };
  const badgeFor = (i) => (i.id === "offline" && status.error ? "!" : undefined);
  return (
    <aside className="fixed left-0 top-0 z-40 hidden h-screen w-64 flex-col border-r border-zinc-200 bg-[#F6F5F0]/80 px-4 pb-28 pt-7 backdrop-blur-md md:flex" data-testid="sidebar">
      <div className="flex items-center gap-2 px-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-zinc-900 text-white">
          <AudioLines size={14} />
        </div>
        <span className="truncate whitespace-nowrap font-heading text-base font-bold tracking-tight" data-testid="sidebar-brand">Resonance Harbor</span>
      </div>
      <SidebarStorage />
      <div ref={scrollRef} className="sidebar-scroll relative mt-3 flex-1 overflow-y-auto px-1" data-testid="sidebar-scroll">
        <PinStrip pins={pins} dragging={dragging} byId={byId} {...dragProps} />
        {!pins.includes(RECENT.id) && <div className="mb-1" data-testid="sidebar-top-items"><Item item={RECENT} {...dragProps} /></div>}
        {lib.length > 0 && (
          <Group id="library" label="Library" items={lib.length} popup={() => lib.map((i) => <Item key={i.id} item={i} prefix="sidebar-popup-item" />)}>
            {(collapsed) => lib.filter((i) => !collapsed || isActive(i)).map((i) => <Item key={i.id} item={i} {...dragProps} />)}
          </Group>
        )}
        {tools.length > 0 && (
          <Group id="tools" label="Tools" items={tools.length} popup={() => tools.map((i) => <Item key={i.id} item={i} prefix="sidebar-popup-item" badge={badgeFor(i)} />)}>
            {(collapsed) => tools.filter((i) => !collapsed || isActive(i)).map((i) => <Item key={i.id} item={i} badge={badgeFor(i)} {...dragProps} />)}
          </Group>
        )}
        {!pins.includes(FAVORITES.id) && <div className="mt-5" data-testid="sidebar-favorites-item"><Item item={FAVORITES} {...dragProps} /></div>}
        {pls.length > 0 && (
          <Group id="playlists" label="Your playlists" items={pls.length} popup={() => pls.map((i) => <Item key={i.id} item={{ ...i, testid: `sidebar-popup-playlist-${i.id.slice(9)}` }} />)}>
            {(collapsed) => <div className="pb-2">{pls.filter((i) => !collapsed || pathname === i.to).map((i) => <Item key={i.id} item={i} {...dragProps} />)}</div>}
          </Group>
        )}
      </div>
      <SidebarResume />
    </aside>
  );
};

export const MobileNav = () => {
  const { user } = useAuth();
  return (
  <nav className="sticky top-0 z-30 flex gap-1 overflow-x-auto border-b border-zinc-200 bg-white/90 px-3 py-2 backdrop-blur md:hidden" data-testid="mobile-nav">
    {[RECENT, ...LIBRARY, ...toolsFor(user), FAVORITES].map((i) => (
      <NavLink key={i.id} to={i.to} className="whitespace-nowrap rounded-full px-3 py-1 text-xs text-zinc-600 hover:bg-zinc-100">{i.label}</NavLink>
    ))}
  </nav>
  );
};
